import {
  acquisitionInvestorsDollarCommonEquityAtom,
  acquisitionInvestorsDollarPreferredEquityAtom,
  acquisitionInvestorsPercentCommonEquityAtom,
  acquisitionInvestorsPercentPreferredEquityAtom,
  coInvestorsPercentPreferredEquityAtom,
  commonEquityTotalPercentAtom,
  preferredEquityTotalPercentAtom,
  promoteSearchersCarryDollarCommonEquityAtom,
  promoteSearchersCarryPercentCommonEquityAtom,
  searcherCoInvestDollarCommonEquityAtom,
  searcherCoInvestDollarPreferredEquityAtom,
  searcherCoInvestPercentCommonEquityAtom,
  searchPhaseInvestorsDollarCommonEquityAtom,
  searchPhaseInvestorsDollarPreferredEquityAtom,
  searchPhaseInvestorsPercentCommonEquityAtom,
  searchPhaseInvestorsPercentPreferredEquityAtom,
  sellerRolloverDollarCommonEquityAtom,
  sellerRolloverDollarPreferredEquityAtom,
  sellerRolloverPercentCommonEquityAtom,
  sellerRolloverPercentPreferredEquityAtom,
  totalCapitalInvestedAtom,
  totalCommonEquityDollarValueAtom,
  totalPreferredEquityDollarValueAtom,
} from "@/financeModels/owasco/companyOwnership";
import { isExternalEquityAtom } from "@/financeModels/owasco/general";
import {
  allInvestorTypesAtom,
  basicReturnMetricsAtoms,
  cashOnCashTableListAtom,
  coReturnMetricsAtoms,
  cumulativePrincipalRepaidTableListAtom,
  investorDistributionTableListAtom,
  ReturnMetricsAtoms,
} from "@/financeModels/owasco/returnMetrics";
import { atom, Getter } from "jotai";
import {
  boldStyle,
  createEmptyRow,
  createGroupEndRow,
  createGroupStartRow,
  createTitleRow,
  greyHighlightStyle,
  groupWorksheetRows,
  SpreadsheetCellStyle,
} from "./helper";

const trueAtom = atom(true);

const XLSXStub = () => import("xlsx-js-style");
type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;

const currencyFormat = '"$"#,##0;[Red]("$"#,##0)';
const percentFormat = "0.00%";
const moicFormat = "0.00x";

const stringCell = (value: string, style?: SpreadsheetCellStyle) => ({
  v: value,
  t: "s",
  s: style,
});

const numberCell = (
  value: unknown,
  format: string,
  style?: SpreadsheetCellStyle,
) => {
  const numericValue = Number(value);

  if (value === null || value === undefined || Number.isNaN(numericValue)) {
    return stringCell("", style);
  }

  return {
    v: Number(numericValue.toFixed(6)),
    t: "n",
    z: format,
    s: style,
  };
};

const currencyCell = (value: unknown, style?: SpreadsheetCellStyle) =>
  numberCell(value, currencyFormat, style);

const percentCell = (value: unknown, style?: SpreadsheetCellStyle) =>
  numberCell(value, percentFormat, style);

const moicCell = (value: unknown, style?: SpreadsheetCellStyle) => {
  const numericValue = Number(value);

  if (
    value === null ||
    value === undefined ||
    Number.isNaN(numericValue) ||
    numericValue === 0 ||
    numericValue <= -10000 ||
    numericValue >= 10000
  ) {
    return stringCell("-", style);
  }

  return numberCell(numericValue, moicFormat, style);
};

const labelizeInvestorTitle = (title: string) =>
  title.replace("All Investors", "All Stakeholders");

const formatStepUpMultiple = (
  multiplier: number | null,
  stepUpElection: boolean | null,
  equityType: "preferred" | "common" | null,
  stepUpApplies: boolean | null,
) => {
  if (
    multiplier === null ||
    !stepUpElection ||
    !stepUpApplies ||
    equityType === "common"
  ) {
    return "-";
  }

  const numericMultiplier = Number(multiplier);
  if (Number.isNaN(numericMultiplier)) {
    return "-";
  }

  const normalized = Number((numericMultiplier / 100).toFixed(2));
  return `${normalized}x`;
};

const formatEquityClass = (equityType: "preferred" | "common" | null) => {
  if (!equityType) return "";
  return equityType === "common"
    ? "Common Equity"
    : "Preferred & Common Equity";
};

const buildCapTableRows = (get: Getter) => {
  const investorTypes = get(allInvestorTypesAtom);
  const isExternalEquity = get(isExternalEquityAtom);
  const visibleInvestorIds = new Set(
    investorTypes
      .filter((investor) => investor.isVisible)
      .map((investor) => investor.id),
  );

  const rows = [
    {
      id: "searchPhase" as const,
      preferredEquityValue: get(searchPhaseInvestorsDollarPreferredEquityAtom),
      commonEquityValue: get(searchPhaseInvestorsDollarCommonEquityAtom),
      preferredOwnershipPercent: get(
        searchPhaseInvestorsPercentPreferredEquityAtom,
      ),
      commonOwnershipPercent: get(searchPhaseInvestorsPercentCommonEquityAtom),
    },
    {
      id: "acquisition" as const,
      preferredEquityValue: get(acquisitionInvestorsDollarPreferredEquityAtom),
      commonEquityValue: get(acquisitionInvestorsDollarCommonEquityAtom),
      preferredOwnershipPercent: get(
        acquisitionInvestorsPercentPreferredEquityAtom,
      ),
      commonOwnershipPercent: get(acquisitionInvestorsPercentCommonEquityAtom),
    },
    {
      id: "coinvest" as const,
      preferredEquityValue: get(searcherCoInvestDollarPreferredEquityAtom),
      commonEquityValue: get(searcherCoInvestDollarCommonEquityAtom),
      preferredOwnershipPercent: get(coInvestorsPercentPreferredEquityAtom),
      commonOwnershipPercent: isExternalEquity
        ? get(searcherCoInvestPercentCommonEquityAtom)
        : 1,
    },
    {
      id: "rollover" as const,
      preferredEquityValue: get(sellerRolloverDollarPreferredEquityAtom),
      commonEquityValue: get(sellerRolloverDollarCommonEquityAtom),
      preferredOwnershipPercent: get(sellerRolloverPercentPreferredEquityAtom),
      commonOwnershipPercent: get(sellerRolloverPercentCommonEquityAtom),
    },
    {
      id: "promote" as const,
      preferredEquityValue: null,
      commonEquityValue: get(promoteSearchersCarryDollarCommonEquityAtom),
      preferredOwnershipPercent: null,
      commonOwnershipPercent: get(promoteSearchersCarryPercentCommonEquityAtom),
    },
  ]
    .filter((row) => visibleInvestorIds.has(row.id))
    .map((row) => {
      const investor = investorTypes.find(
        (candidate) => candidate.id === row.id,
      );

      return [
        stringCell(investor?.title || row.id),
        stringCell(formatEquityClass(investor?.equityType ?? null)),
        currencyCell(investor?.investedCapital, undefined),
        stringCell(
          formatStepUpMultiple(
            investor?.multiplier ? Number(investor.multiplier) : null,
            investor?.stepUpElection ?? null,
            investor?.equityType ?? null,
            investor?.stepUpApplies ?? null,
          ),
        ),
        currencyCell(row.preferredEquityValue),
        currencyCell(row.commonEquityValue),
        percentCell(row.preferredOwnershipPercent),
        percentCell(row.commonOwnershipPercent),
      ];
    });

  rows.push([
    stringCell("Total", greyHighlightStyle),
    stringCell("", greyHighlightStyle),
    currencyCell(get(totalCapitalInvestedAtom), greyHighlightStyle),
    stringCell("", greyHighlightStyle),
    currencyCell(get(totalPreferredEquityDollarValueAtom), greyHighlightStyle),
    currencyCell(get(totalCommonEquityDollarValueAtom), greyHighlightStyle),
    percentCell(get(preferredEquityTotalPercentAtom), greyHighlightStyle),
    percentCell(get(commonEquityTotalPercentAtom), greyHighlightStyle),
  ]);

  return rows;
};

const resolveReturnMetricsRows = (get: Getter, rows: ReturnMetricsAtoms[]) => {
  return rows
    .map((row) => ({
      title: row.title,
      visible: get(row.isVisible ?? trueAtom),
      preferredOwnershipPercent: get(row.preferredOwnershipPercent),
      commonOwnershipPercent: get(row.commonOwnershipPercent),
      totalDistributionsTotal: get(row.totalDistributionsTotal),
      totalDistributionsPreferred: get(row.totalDistributionsPreferred),
      totalDistributionsCommon: get(row.totalDistributionsCommon),
      saleProceedsCommon: get(row.saleProceedsCommon),
      totalReturnTotal: get(row.totalReturnTotal),
      totalReturnPreferred: get(row.totalReturnPreferred),
      totalReturnCommon: get(row.totalReturnCommon),
      irr: get(row.irr),
      moic: get(row.moic),
    }))
    .filter((row) => row.visible);
};

const buildReturnMetricsSectionRows = (
  get: Getter,
  rows: ReturnMetricsAtoms[],
) => {
  return resolveReturnMetricsRows(get, rows).flatMap((row) => {
    return [
      [
        stringCell(row.title, boldStyle),
        stringCell("Total", boldStyle),
        percentCell(row.preferredOwnershipPercent, boldStyle),
        percentCell(row.commonOwnershipPercent, boldStyle),
        currencyCell(row.totalDistributionsTotal, boldStyle),
        currencyCell(row.saleProceedsCommon, boldStyle),
        currencyCell(row.totalReturnTotal, boldStyle),
        percentCell(row.irr, boldStyle),
        moicCell(row.moic, boldStyle),
      ],
      createGroupStartRow(),
      [
        stringCell("  Preferred Equity"),
        stringCell("Preferred"),
        percentCell(row.preferredOwnershipPercent),
        percentCell(0),
        currencyCell(row.totalDistributionsPreferred),
        stringCell("-"),
        currencyCell(row.totalReturnPreferred),
        stringCell("-"),
        stringCell("-"),
      ],
      [
        stringCell("  Common Equity"),
        stringCell("Common"),
        percentCell(0),
        percentCell(row.commonOwnershipPercent),
        currencyCell(row.totalDistributionsCommon),
        currencyCell(row.saleProceedsCommon),
        currencyCell(row.totalReturnCommon),
        stringCell("-"),
        stringCell("-"),
      ],
      createGroupEndRow(),
    ];
  });
};

const buildDistributionRows = (get: Getter) => {
  const investorTypes = get(allInvestorTypesAtom).filter(
    (investor) => investor.isVisible,
  );
  const distributionTables = get(investorDistributionTableListAtom);
  const cashOnCashTables = get(cashOnCashTableListAtom);
  const cumulativePrincipalTables = get(cumulativePrincipalRepaidTableListAtom);

  return investorTypes.flatMap((investor) => {
    const distributionTable = distributionTables.find(
      (table) => table.id === investor.id,
    );
    const cashOnCashTable = cashOnCashTables.find(
      (table) => table.id === investor.id,
    );
    const cumulativePrincipalTable = cumulativePrincipalTables.find(
      (table) => table.id === investor.id,
    );

    if (!distributionTable || !cashOnCashTable || !cumulativePrincipalTable) {
      return [];
    }

    const rows: unknown[][] = [
      createTitleRow(
        `DISTRIBUTIONS - ${labelizeInvestorTitle(
          distributionTable.title,
        ).toUpperCase()}`,
      ),
      createEmptyRow(),
    ];

    if (investor.id !== "all") {
      const stepUpMultiple = formatStepUpMultiple(
        investor.multiplier ? Number(investor.multiplier) : null,
        investor.stepUpElection,
        investor.equityType,
        investor.stepUpApplies,
      );

      rows.push([
        stringCell("Equity Class", boldStyle),
        stringCell(formatEquityClass(investor.equityType)),
      ]);
      rows.push([
        stringCell("Equity Step-Up", boldStyle),
        stringCell(stepUpMultiple === "-" ? "None" : stepUpMultiple),
      ]);
      rows.push([
        stringCell("Invested Capital", boldStyle),
        currencyCell(investor.investedCapital),
      ]);
      rows.push(createEmptyRow());
    }

    rows.push([
      stringCell("Period", boldStyle),
      stringCell("Year", boldStyle),
      stringCell("Preferred Dividends", boldStyle),
      stringCell("Principal Repayments", boldStyle),
      stringCell("Profit Distributions", boldStyle),
      stringCell("Tax Distributions", boldStyle),
      stringCell("Net Sale Proceeds", boldStyle),
      stringCell("Total Investor Distributions", boldStyle),
      stringCell("Cumulative Principal Repaid", boldStyle),
      stringCell("Cash-On-Cash Return", boldStyle),
    ]);

    const periodRows = distributionTable.periods.map((period, index) => {
      const cumulativePrincipalValue =
        cumulativePrincipalTable.cumulativePrincipalRepaid[index];

      return [
        stringCell(String(period)),
        stringCell(String(distributionTable.years[index] ?? "")),
        currencyCell(distributionTable.preferredDividends[index]),
        currencyCell(distributionTable.principalRepayments[index]),
        currencyCell(distributionTable.profitDistributions[index]),
        currencyCell(distributionTable.taxDistributions[index]),
        currencyCell(distributionTable.proceedsFromCompanySale[index]),
        currencyCell(distributionTable.totalInvestorDistributions[index]),
        cumulativePrincipalValue === null
          ? stringCell("Principal Returned")
          : currencyCell(cumulativePrincipalValue),
        percentCell(cashOnCashTable.cashOnCash[index]),
      ];
    });

    rows.push(...periodRows);
    rows.push([
      stringCell("", greyHighlightStyle),
      stringCell("Total", greyHighlightStyle),
      currencyCell(
        distributionTable.totals.preferredDividends,
        greyHighlightStyle,
      ),
      currencyCell(
        distributionTable.totals.principalRepayments,
        greyHighlightStyle,
      ),
      currencyCell(
        distributionTable.totals.profitDistributions,
        greyHighlightStyle,
      ),
      currencyCell(
        distributionTable.totals.taxDistributions,
        greyHighlightStyle,
      ),
      currencyCell(
        distributionTable.totals.proceedsFromCompanySale,
        greyHighlightStyle,
      ),
      currencyCell(
        distributionTable.totals.totalInvestorDistributions,
        greyHighlightStyle,
      ),
      stringCell("", greyHighlightStyle),
      percentCell(cashOnCashTable.totalCashOnCash, greyHighlightStyle),
    ]);
    rows.push(createEmptyRow());

    return rows;
  });
};

export const buildROIWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const pageTitle = (window.document.title || "ETA IQ ROI Metrics")
    .replace("Model (", "")
    .replace(")", "");

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),
    createTitleRow("CAP TABLE"),
    createEmptyRow(),
    [
      stringCell("Investor Type", boldStyle),
      stringCell("Equity Class", boldStyle),
      stringCell("Invested Capital", boldStyle),
      stringCell("Step-Up Multiple", boldStyle),
      stringCell("Post Step-Up Preferred Equity", boldStyle),
      stringCell("Post Step-Up Common Equity", boldStyle),
      stringCell("Preferred Equity %", boldStyle),
      stringCell("Common Equity %", boldStyle),
    ],
    ...buildCapTableRows(get),
    createEmptyRow(),
    createTitleRow("RETURN METRICS - SUMMARY"),
    createEmptyRow(),
    [
      stringCell("Stakeholder", boldStyle),
      stringCell("Equity Slice", boldStyle),
      stringCell("Preferred Ownership", boldStyle),
      stringCell("Common Ownership", boldStyle),
      stringCell("Total Distributions", boldStyle),
      stringCell("Sale Proceeds", boldStyle),
      stringCell("Total Return", boldStyle),
      stringCell("IRR", boldStyle),
      stringCell("MOIC", boldStyle),
    ],
    ...buildReturnMetricsSectionRows(get, coReturnMetricsAtoms),
    createEmptyRow(),
    createTitleRow("RETURN METRICS - BREAKDOWN BY INVESTOR TYPE"),
    createEmptyRow(),
    [
      stringCell("Stakeholder", boldStyle),
      stringCell("Equity Slice", boldStyle),
      stringCell("Preferred Ownership", boldStyle),
      stringCell("Common Ownership", boldStyle),
      stringCell("Total Distributions", boldStyle),
      stringCell("Sale Proceeds", boldStyle),
      stringCell("Total Return", boldStyle),
      stringCell("IRR", boldStyle),
      stringCell("MOIC", boldStyle),
    ],
    ...buildReturnMetricsSectionRows(get, basicReturnMetricsAtoms),
    createEmptyRow(),
    ...buildDistributionRows(get),
  ];

  const rowGroups: { start: number; end: number }[] = [];
  const cleanedRows: typeof rows = [];
  let currentGroupStartIndex: number | null = null;

  rows.forEach((row) => {
    if (row[0] === "GROUP_START") {
      currentGroupStartIndex = cleanedRows.length;
    } else if (row[0] === "GROUP_END") {
      if (currentGroupStartIndex !== null) {
        rowGroups.push({
          start: currentGroupStartIndex,
          end: cleanedRows.length - 1,
        });
        currentGroupStartIndex = null;
      }
    } else {
      cleanedRows.push(row);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(cleanedRows);

  ws["!cols"] = [
    { wpx: 240 },
    { wpx: 110 },
    { wpx: 130 },
    { wpx: 130 },
    { wpx: 160 },
    { wpx: 160 },
    { wpx: 120 },
    { wpx: 120 },
    { wpx: 120 },
    { wpx: 120 },
  ];

  rowGroups.forEach(({ start, end }) => groupWorksheetRows(ws, start, end));

  return ws;
};

export const roiExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildROIWorksheet(get, XLSX);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "ROI Metrics");

  const fmt = "xlsx";
  const filename = `roi-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
